<template>
  <div class="min-h-screen bg-theme-bg2 py-4 sm:py-8 lg:py-12 px-3 sm:px-6 font-sans">
    <div class="max-w-5xl mx-auto space-y-6">
      <!-- Header Section -->
      <div class="bg-gradient-to-r from-primary-400 to-primary-600 text-white rounded-2xl shadow-lg p-5 sm:p-8 text-center">
        <h2 class="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-wide">Item Manager</h2>
        <p class="mt-2 text-sm sm:text-base text-white/80">Effortlessly manage your items and track rentals.</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
        <p class="mt-3 text-theme-text2">Loading items and rentals...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="space-y-8 sm:space-y-12">

        <!-- ==================== Equipment Gallery ==================== -->
        <section>
          <h2 class="text-xl sm:text-2xl lg:text-3xl font-bold text-theme-text mb-4 sm:mb-6 flex items-center gap-2">
            <i class="pi pi-box text-primary-500"></i> Your Items
          </h2>

          <div
            v-if="store.userEquipments.length > 0"
            class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6"
          >
            <div
              v-for="equipment in store.userEquipments"
              :key="equipment.id"
              @click="goToDetail(equipment.id)"
              class="bg-theme-card rounded-xl border border-theme-border overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer group"
            >
              <div class="relative aspect-[4/3]">
                <img
                  v-if="equipment.images?.length > 0"
                  :src="resolveImageUrl(equipment.images[0].image_url)"
                  :alt="equipment.name || 'Equipment'"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <img
                  v-else
                  src="../assets/images/placeholder.png"
                  alt="No image"
                  class="w-full h-full object-cover opacity-70"
                />
                <span
                  :class="equipment.is_available ? 'bg-green-500' : 'bg-neutral-500'"
                  class="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold text-white flex items-center gap-1"
                >
                  <i :class="equipment.is_available ? 'pi pi-check' : 'pi pi-times'" class="text-[10px]"></i>
                  <span class="hidden sm:inline">{{ equipment.is_available ? 'Available' : 'Unavailable' }}</span>
                </span>
              </div>
              <div class="p-3 sm:p-4">
                <h3 class="text-sm sm:text-base font-semibold text-theme-text truncate">{{ equipment.name || 'Unnamed' }}</h3>
                <p class="text-primary-600 text-xs sm:text-sm font-medium mt-0.5">{{ formatPrice(equipment.hourly_rate || 0) }} / day</p>
                <button
                  v-if="!isLockedByRental(equipment.id)"
                  @click.stop="openEditModal(equipment)"
                  class="mt-2 flex items-center gap-1.5 text-xs font-semibold text-theme-muted hover:text-primary-600 border border-theme-border hover:border-primary-400 rounded-lg px-2.5 py-1.5 transition bg-theme-bg2 hover:bg-primary-600/5"
                >
                  <i class="pi pi-pencil text-xs"></i>
                  Edit
                </button>
              </div>
            </div>
          </div>

          <div v-else class="bg-theme-card rounded-xl border border-theme-border p-8 sm:p-12 text-center">
            <i class="pi pi-box text-4xl sm:text-5xl text-theme-muted mb-4"></i>
            <p class="text-base sm:text-lg text-theme-text">No items listed yet.</p>
            <RouterLink
              :to="{ name: 'list-item' }"
              class="inline-block mt-4 px-6 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition text-sm"
            >
              List Your First Item
            </RouterLink>
          </div>
        </section>

        <!-- ==================== Rental Overview ==================== -->
        <section>
          <h2 class="text-xl sm:text-2xl lg:text-3xl font-bold text-theme-text mb-4 sm:mb-6 flex items-center gap-2">
            <i class="pi pi-calendar text-primary-500"></i> Rental Overview
          </h2>

          <div class="space-y-5 sm:space-y-6">

            <!-- ===== Pending Approval ===== -->
            <div class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-theme-border flex items-center justify-between bg-theme-bg2/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-clock text-yellow-500 text-sm"></i> Pending Approval
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-yellow-100 text-yellow-700">{{ lists.pending.length }} item(s)</span>
              </div>
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-theme-bg2 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-theme-border">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.pending.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No pending items.</td>
                    </tr>
                    <tr
                      v-for="item in lists.pending"
                      :key="item.id"
                      class="border-b border-theme-border last:border-b-0 hover:bg-theme-bg2/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border text-theme-text font-medium">({{ item.quantity || 1 }}) {{ item.item?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(item) }} &ndash; {{ getEnd(item) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(item) }}</td>
                      <td class="py-3 px-4 text-center">
                        <button
                          @click="openApprovalModal(item)"
                          class="px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs font-medium"
                          :disabled="isUpdating"
                        >
                          Approve
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="sm:hidden divide-y divide-theme-border">
                <div v-if="lists.pending.length === 0" class="py-8 text-center text-theme-muted text-sm">No pending items.</div>
                <div v-for="item in lists.pending" :key="item.id" class="p-4 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <span class="font-medium text-theme-text text-sm">({{ item.quantity || 1 }}) {{ item.item?.name || 'N/A' }}</span>
                    <span class="font-semibold text-theme-text text-sm">{{ getTotal(item) }}</span>
                  </div>
                  <div class="text-xs text-theme-text2">{{ getStart(item) }} &ndash; {{ getEnd(item) }}</div>
                  <button
                    @click="openApprovalModal(item)"
                    class="w-full mt-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs font-medium"
                    :disabled="isUpdating"
                  >
                    Approve
                  </button>
                </div>
              </div>
            </div>

            <!-- ===== Paid (Payment Received) ===== -->
            <div class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-theme-border flex items-center justify-between bg-theme-bg2/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-credit-card text-primary-500 text-sm"></i> Paid
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-primary-100 text-primary-700">{{ lists.paid.length }} item(s)</span>
              </div>
              <!-- Desktop Table -->
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-theme-bg2 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-theme-border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.paid.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No paid items.</td>
                    </tr>
                    <tr
                      v-for="x in lists.paid"
                      :key="x.id"
                      class="border-b border-theme-border last:border-b-0 hover:bg-theme-bg2/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border text-theme-text font-medium">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(x) }}</td>
                      <td class="py-3 px-4 text-center">
                        <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-primary-100 text-primary-700">
                          <i class="pi pi-credit-card text-[10px]"></i> Paid
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Mobile Cards -->
              <div class="sm:hidden divide-y divide-theme-border">
                <div v-if="lists.paid.length === 0" class="py-8 text-center text-theme-muted text-sm">No paid items.</div>
                <div v-for="x in lists.paid" :key="x.id" class="p-4 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <span class="font-medium text-theme-text text-sm">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</span>
                    <span class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap shrink-0">
                      <i class="pi pi-credit-card text-[8px]"></i> Paid
                    </span>
                  </div>
                  <div class="flex justify-between text-xs">
                    <span class="text-theme-text2">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</span>
                    <span class="font-semibold text-theme-text">{{ getTotal(x) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== Awaiting Pickup ===== -->
            <div class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-theme-border flex items-center justify-between bg-theme-bg2/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-truck text-blue-500 text-sm"></i> Awaiting Pickup
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-blue-100 text-blue-700">{{ lists.awaitingPickup.length }} item(s)</span>
              </div>
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-theme-bg2 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-theme-border">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.awaitingPickup.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No items awaiting pickup.</td>
                    </tr>
                    <tr
                      v-for="x in lists.awaitingPickup"
                      :key="x.id"
                      class="border-b border-theme-border last:border-b-0 hover:bg-theme-bg2/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border">
                        <span class="text-theme-text font-medium">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</span>
                        <div class="text-xs text-theme-muted mt-0.5">Status: <span class="font-semibold capitalize">{{ x.status }}</span></div>
                      </td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(x) }}</td>
                      <td class="py-3 px-4 text-center">
                        <button
                          v-if="x.status === 'pickup'"
                          @click="openPickupConfirmationModal(x.id)"
                          class="px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs font-medium"
                          :disabled="isUpdating"
                        >
                          Confirm Pickup
                        </button>
                        <span v-else class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                          <i class="pi pi-clock text-[10px]"></i> Waiting
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="sm:hidden divide-y divide-theme-border">
                <div v-if="lists.awaitingPickup.length === 0" class="py-8 text-center text-theme-muted text-sm">No items awaiting pickup.</div>
                <div v-for="x in lists.awaitingPickup" :key="x.id" class="p-4 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <div>
                      <span class="font-medium text-theme-text text-sm">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</span>
                      <div class="text-xs text-theme-muted mt-0.5">Status: <span class="font-semibold capitalize">{{ x.status }}</span></div>
                    </div>
                    <span class="font-semibold text-theme-text text-sm whitespace-nowrap shrink-0">{{ getTotal(x) }}</span>
                  </div>
                  <div class="text-xs text-theme-text2">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</div>
                  <div class="pt-1">
                    <button
                      v-if="x.status === 'pickup'"
                      @click="openPickupConfirmationModal(x.id)"
                      class="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs font-medium"
                      :disabled="isUpdating"
                    >
                      Confirm Pickup
                    </button>
                    <span v-else class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                      <i class="pi pi-clock text-[10px]"></i> Waiting
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <NewPickupConfirmationModal
              :showPickupModal="showNewPickupConfirmationModal"
              :orderItem="selectedOrderItem"
              @close="showNewPickupConfirmationModal = false"
              @confirm="handlePickupConfirmation"
            />

            <!-- ===== Currently Rented ===== -->
            <div class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-theme-border flex items-center justify-between bg-theme-bg2/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-check-circle text-green-500 text-sm"></i> Currently Rented
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-green-100 text-green-700">{{ lists.rented.length }} item(s)</span>
              </div>
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-theme-bg2 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-theme-border">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.rented.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No rented items.</td>
                    </tr>
                    <tr
                      v-for="rental in lists.rented"
                      :key="rental.id"
                      class="border-b border-theme-border last:border-b-0 hover:bg-theme-bg2/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border">
                        <span class="text-theme-text font-medium">({{ rental.quantity || 1 }}) {{ rental.item?.name || 'N/A' }}</span>
                        <div class="text-xs text-theme-muted mt-0.5">Status: <span class="font-semibold capitalize">{{ rental.status }}</span></div>
                      </td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(rental) }} &ndash; {{ getEnd(rental) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(rental) }}</td>
                      <td class="py-3 px-4 text-center">
                        <button
                          v-if="rental.status === 'return'"
                          @click="openReturnModal(rental.id)"
                          class="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs font-medium"
                          :disabled="isUpdating"
                        >
                          Confirm Return
                        </button>
                        <span v-else class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-green-100 text-green-700">
                          <i class="pi pi-check text-[10px]"></i> Active
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="sm:hidden divide-y divide-theme-border">
                <div v-if="lists.rented.length === 0" class="py-8 text-center text-theme-muted text-sm">No rented items.</div>
                <div v-for="rental in lists.rented" :key="rental.id" class="p-4 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <div>
                      <span class="font-medium text-theme-text text-sm">({{ rental.quantity || 1 }}) {{ rental.item?.name || 'N/A' }}</span>
                      <div class="text-xs text-theme-muted mt-0.5">Status: <span class="font-semibold capitalize">{{ rental.status }}</span></div>
                    </div>
                    <span class="font-semibold text-theme-text text-sm whitespace-nowrap shrink-0">{{ getTotal(rental) }}</span>
                  </div>
                  <div class="text-xs text-theme-text2">{{ getStart(rental) }} &ndash; {{ getEnd(rental) }}</div>
                  <div class="pt-1">
                    <button
                      v-if="rental.status === 'return'"
                      @click="openReturnModal(rental.id)"
                      class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs font-medium"
                      :disabled="isUpdating"
                    >
                      Confirm Return
                    </button>
                    <span v-else class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-green-100 text-green-700">
                      <i class="pi pi-check text-[10px]"></i> Active
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <NewReturnConfirmationModal
              :showReturnModal="showReturnModal"
              :orderItemId="selectedOrderItem"
              @close="closeReturnModal"
              @confirm="handleReturnConfirmation"
            />

            <!-- ===== Disputed Items ===== -->
            <div class="bg-theme-card rounded-xl border border-red-200 shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-red-200 flex items-center justify-between bg-red-50/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-exclamation-triangle text-red-500 text-sm"></i> Disputed Items
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-red-100 text-red-700">{{ lists.disputed.length }} item(s)</span>
              </div>
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-red-50/30 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-red-200">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-red-200">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-red-200">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-red-200">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.disputed.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No disputed items.</td>
                    </tr>
                    <tr
                      v-for="x in lists.disputed"
                      :key="x.id"
                      class="border-b border-red-100 last:border-b-0 hover:bg-red-50/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border text-theme-text font-medium">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(x) }}</td>
                      <td class="py-3 px-4 text-center">
                        <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-red-100 text-red-700 capitalize">
                          <i class="pi pi-exclamation-triangle text-[10px]"></i> {{ x.status || 'N/A' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="sm:hidden divide-y divide-red-100">
                <div v-if="lists.disputed.length === 0" class="py-8 text-center text-theme-muted text-sm">No disputed items.</div>
                <div v-for="x in lists.disputed" :key="x.id" class="p-4 space-y-2 bg-red-50/20">
                  <div class="flex items-start justify-between gap-2">
                    <span class="font-medium text-theme-text text-sm">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</span>
                    <span class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 capitalize whitespace-nowrap shrink-0">
                      <i class="pi pi-exclamation-triangle text-[8px]"></i> {{ x.status || 'N/A' }}
                    </span>
                  </div>
                  <div class="flex justify-between text-xs">
                    <span class="text-theme-text2">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</span>
                    <span class="font-semibold text-theme-text">{{ getTotal(x) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== Returned ===== -->
            <div class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-theme-border flex items-center justify-between bg-theme-bg2/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-replay text-purple-500 text-sm"></i> Returned
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-purple-100 text-purple-700">{{ lists.returned.length }} item(s)</span>
              </div>
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-theme-bg2 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-theme-border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.returned.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No returned items.</td>
                    </tr>
                    <tr
                      v-for="x in lists.returned"
                      :key="x.id"
                      class="border-b border-theme-border last:border-b-0 hover:bg-theme-bg2/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border text-theme-text font-medium">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(x) }}</td>
                      <td class="py-3 px-4 text-center">
                        <span class="inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full bg-purple-100 text-purple-700 capitalize">{{ x.status || 'N/A' }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="sm:hidden divide-y divide-theme-border">
                <div v-if="lists.returned.length === 0" class="py-8 text-center text-theme-muted text-sm">No returned items.</div>
                <div v-for="x in lists.returned" :key="x.id" class="p-4 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <span class="font-medium text-theme-text text-sm">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</span>
                    <span class="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 capitalize whitespace-nowrap shrink-0">{{ x.status || 'N/A' }}</span>
                  </div>
                  <div class="flex justify-between text-xs">
                    <span class="text-theme-text2">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</span>
                    <span class="font-semibold text-theme-text">{{ getTotal(x) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== Canceled / Rejected ===== -->
            <div class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-4 sm:px-6 py-3 sm:py-4 border-b border-theme-border flex items-center justify-between bg-theme-bg2/50">
                <h3 class="text-sm sm:text-base lg:text-lg font-semibold text-theme-text flex items-center gap-2">
                  <i class="pi pi-ban text-neutral-500 text-sm"></i> Canceled / Rejected
                </h3>
                <span class="text-[10px] sm:text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-neutral-100 text-neutral-600">{{ lists.canceledRejected.length }} item(s)</span>
              </div>
              <div class="hidden sm:block overflow-x-auto">
                <table class="w-full text-left text-sm">
                  <thead>
                    <tr class="bg-theme-bg2 text-xs uppercase text-theme-muted">
                      <th class="py-3 px-4 font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Total</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-theme-border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="lists.canceledRejected.length === 0">
                      <td colspan="4" class="py-8 text-center text-theme-muted">No canceled/rejected items.</td>
                    </tr>
                    <tr
                      v-for="x in lists.canceledRejected"
                      :key="x.id"
                      class="border-b border-theme-border last:border-b-0 hover:bg-theme-bg2/30 transition-colors"
                    >
                      <td class="py-3 px-4 border-r border-theme-border text-theme-text font-medium">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text2 text-xs">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</td>
                      <td class="py-3 px-4 text-center border-r border-theme-border text-theme-text font-semibold">{{ getTotal(x) }}</td>
                      <td class="py-3 px-4 text-center">
                        <span
                          :class="x.status === 'canceled' ? 'bg-neutral-100 text-neutral-600' : 'bg-red-100 text-red-700'"
                          class="inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full capitalize"
                        >
                          {{ x.status }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="sm:hidden divide-y divide-theme-border">
                <div v-if="lists.canceledRejected.length === 0" class="py-8 text-center text-theme-muted text-sm">No canceled/rejected items.</div>
                <div v-for="x in lists.canceledRejected" :key="x.id" class="p-4 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <span class="font-medium text-theme-text text-sm">({{ x.quantity || 1 }}) {{ x.item?.name || 'N/A' }}</span>
                    <span
                      :class="x.status === 'canceled' ? 'bg-neutral-100 text-neutral-600' : 'bg-red-100 text-red-700'"
                      class="text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize whitespace-nowrap shrink-0"
                    >
                      {{ x.status }}
                    </span>
                  </div>
                  <div class="flex justify-between text-xs">
                    <span class="text-theme-text2">{{ getStart(x) }} &ndash; {{ getEnd(x) }}</span>
                    <span class="font-semibold text-theme-text">{{ getTotal(x) }}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>
      </div>

      <!-- ==================== Edit Modal ==================== -->
      <transition name="modal">
        <div
          v-if="editModalVisible"
          class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3 sm:px-4"
          @click.self="closeEditModal"
        >
          <div class="bg-theme-card rounded-2xl p-5 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl border border-theme-border">
            <h3 class="text-xl sm:text-2xl font-bold text-theme-text mb-5 sm:mb-6">Edit Equipment</h3>

            <form @submit.prevent="updateEquipment" class="space-y-4">

              <!-- Name -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Name <span class="text-red-500">*</span></label>
                <input v-model="editedEquipment.name" type="text" required
                  class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition" />
              </div>

              <!-- Category -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Category</label>
                <select v-model="editedEquipment.category"
                  class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition">
                  <option v-for="cat in store.categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>

              <!-- Daily Rate -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Daily Rate ($) <span class="text-red-500">*</span></label>
                <input v-model="editedEquipment.hourly_rate" type="number" min="0" step="0.01" required
                  class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition" />
              </div>

              <!-- Available Quantity -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Available Quantity <span class="text-red-500">*</span></label>
                <input v-model="editedEquipment.available_quantity" type="number" min="1" required
                  class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition" />
              </div>

              <!-- Description -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Description</label>
                <div
                  ref="editDescriptionEditorRef"
                  class="quill-editor-wrap rounded-lg border bg-theme-input text-theme-text"
                ></div>
              </div>

              <!-- Terms -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Terms</label>
                <div
                  ref="editTermsEditorRef"
                  class="quill-editor-wrap rounded-lg border bg-theme-input text-theme-text"
                ></div>
              </div>

              <!-- Tags -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Tags <span class="text-theme-muted text-xs">(comma-separated)</span></label>
                <input v-model="tagsInput" type="text" placeholder="e.g. excavator, heavy, construction"
                  class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
              </div>

              <!-- Availability -->
              <div class="flex items-center gap-3">
                <label class="text-sm font-medium text-theme-text2">Available</label>
                <input v-model="editedEquipment.is_available" type="checkbox" class="w-4 h-4 accent-primary-600" />
              </div>

              <!-- Address -->
              <div class="space-y-2 relative">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-theme-text2">Address</label>
                  <button
                    type="button"
                    @click="showEditMapPicker = true"
                    class="flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 border border-primary-300 hover:border-primary-500 rounded-lg px-2.5 py-1.5 transition bg-primary-600/5 hover:bg-primary-600/10"
                  >
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    Pick on map
                  </button>
                </div>
                <input
                  ref="editAutocompleteInput"
                  v-model="editedEquipment.address.street_address"
                  type="text"
                  autocomplete="off"
                  placeholder="Start typing your address"
                  class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted"
                  @input="onEditAddrInput"
                  @blur="setTimeout(() => clearEditAddrSuggestions(), 200)"
                />
                <!-- Suggestions dropdown -->
                <ul v-if="showEditAddrSuggestions && editAddrSuggestions.length"
                  class="absolute z-50 left-0 right-0 mt-1 bg-theme-card border border-theme-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <li v-for="s in editAddrSuggestions" :key="s.placeId"
                    class="px-3 py-2 hover:bg-theme-bg2 cursor-pointer text-sm"
                    @mousedown.prevent="selectEditAddrSuggestion(s)">
                    <span class="font-medium text-theme-text">{{ s.main }}</span>
                    <span v-if="s.secondary" class="text-theme-muted ml-1">{{ s.secondary }}</span>
                  </li>
                </ul>
                <div class="grid grid-cols-2 gap-2">
                  <input v-model="editedEquipment.address.city" type="text" placeholder="City"
                    class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
                  <input v-model="editedEquipment.address.state" type="text" placeholder="State"
                    class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <input v-model="editedEquipment.address.zip_code" type="text" placeholder="ZIP code"
                    class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
                  <input v-model="editedEquipment.address.country" type="text" placeholder="Country"
                    class="w-full border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
                </div>
              </div>

              <!-- Specifications -->
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-sm font-medium text-theme-text2">Specifications</label>
                  <button type="button" @click="addSpecificationRow"
                    class="text-xs text-primary-600 hover:underline font-medium">+ Add row</button>
                </div>
                <div v-for="(spec, i) in specifications" :key="i" class="flex gap-2 mb-2">
                  <input v-model="spec.name" type="text" placeholder="Name"
                    class="flex-1 border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
                  <input v-model="spec.value" type="text" placeholder="Value"
                    class="flex-1 border border-theme-border bg-theme-input text-theme-text rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition placeholder-theme-muted" />
                  <button type="button" @click="removeSpecificationRow(i)"
                    class="text-red-400 hover:text-red-600 px-2 text-lg">&times;</button>
                </div>
              </div>

              <!-- Current Images -->
              <div v-if="editedEquipment.current_images?.length">
                <label class="block text-sm font-medium text-theme-text2 mb-1">Current Images</label>
                <div class="flex flex-wrap gap-2">
                  <div v-for="img in editedEquipment.current_images" :key="img.id" class="relative">
                    <img :src="resolveImageUrl(img.url)" class="w-20 h-20 object-cover rounded-lg border border-theme-border" />
                    <button type="button" @click="removeImage(img.id)"
                      class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">&times;</button>
                  </div>
                </div>
              </div>

              <!-- Add New Images -->
              <div>
                <label class="block text-sm font-medium text-theme-text2 mb-1">Add Images <span class="text-theme-muted text-xs">(max 4 total, JPEG/PNG)</span></label>
                <input type="file" accept="image/jpeg,image/jpg,image/png" multiple @change="handleImageUpload"
                  class="w-full text-sm text-theme-muted file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" />
              </div>

              <!-- Actions -->
              <div class="flex justify-end gap-3 pt-4 border-t border-theme-border">
                <button type="button" @click="closeEditModal"
                  class="px-5 py-2.5 bg-theme-bg2 text-theme-text2 rounded-lg hover:bg-theme-bg2/80 transition font-medium text-sm"
                  :disabled="isUpdating">Cancel</button>
                <button type="submit"
                  class="px-5 py-2.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition text-sm"
                  :disabled="isUpdating">
                  {{ isUpdating ? 'Saving...' : 'Save Changes' }}
                </button>
              </div>

            </form>
          </div>
        </div>
      </transition>

      <!-- Edit modal map picker -->
      <MapPickerModal
        :show="showEditMapPicker"
        @close="showEditMapPicker = false"
        @confirm="onEditMapConfirm"
      />

      <!-- ==================== Approval Modal ==================== -->
      <transition name="modal">
        <div
          v-if="isApprovalModalOpen"
          class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3 sm:px-4"
          @click.self="isApprovalModalOpen = false"
        >
          <div class="bg-theme-card rounded-2xl p-5 sm:p-6 w-full max-w-md shadow-2xl border border-theme-border">
            <h3 class="text-lg sm:text-xl font-semibold text-theme-text mb-4">Approve Rental</h3>
            <p class="text-theme-text2 mb-6 text-sm">Do you want to approve this rental request?</p>
            <div class="flex justify-end gap-3">
              <button
                @click="isApprovalModalOpen = false"
                class="px-5 py-2.5 bg-theme-bg2 text-theme-text2 rounded-lg hover:bg-theme-bg2/80 transition font-medium text-sm"
                :disabled="isUpdating"
              >
                Cancel
              </button>
              <button
                @click="approveRentalApproval(selectedRental?.id)"
                class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold text-sm"
                :disabled="isUpdating"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useAuthStore } from '../store/auth';
import { useEquipmentsStore } from '../store/equipments';
import { resolveImageUrl } from '@/composables/useImageUrl';
import useNotifications from '@/store/notification.js';
import NewPickupConfirmationModal from '@/components/NewPickupConfirmationModal.vue';
import NewReturnConfirmationModal from '@/components/NewReturnConfirmationModal.vue';
import { useCurrency } from '@/composables/useCurrency';
import { useLocationSearch } from '@/composables/useLocationSearch.js';
import MapPickerModal from '@/components/MapPickerModal.vue';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Items');

const { showNotification } = useNotifications();
const { formatPrice } = useCurrency();
const authStore = useAuthStore();
const store = useEquipmentsStore();
const router = useRouter();
const api_base_url = import.meta.env.VITE_API_BASE_URL;

const editModalVisible = ref(false);
const editAutocompleteInput = ref(null);
const showEditMapPicker = ref(false);
const onEditMapConfirm = (result) => {
  if (!editedEquipment.value) return;
  editedEquipment.value.address = {
    ...editedEquipment.value.address,
    street_address: result.street_address || '',
    city:           result.city           || '',
    state:          result.state          || '',
    zip_code:       result.zip_code       || '',
    country:        result.country        || '',
  };
  showEditMapPicker.value = false;
};
const { suggestions: editAddrSuggestions, showSuggestions: showEditAddrSuggestions, fetchSuggestions: fetchEditAddrSuggestions, resolvePlace: resolveEditAddrPlace, clearSuggestions: clearEditAddrSuggestions } = useLocationSearch();
const editedEquipment = ref({});
const newImages = ref([]);
const removeImageIds = ref([]);
const isUpdating = ref(false);

const showReturnModal = ref(false);
const isApprovalModalOpen = ref(false);
const showNewPickupConfirmationModal = ref(false);

const selectedOrderItem = ref(null);
const selectedRental = ref(null);

const orderItems = ref([]);
const loading = ref(true);

// Tags input (comma-separated)
const tagsInput = ref('');

// Quill editor refs
const editDescriptionEditorRef = ref(null);
const editTermsEditorRef = ref(null);
let editDescriptionQuill = null;
let editTermsQuill = null;

const quillToolbarOptions = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['blockquote'],
  ['link'],
  ['clean'],
];

// Specifications as array of { name, value }
const specifications = ref([]);

let editAutocompleteInstance = null;

// -------------------- small helpers --------------------
const withRetry = async (fn, label = 'task', retryCount = 3) => {
  try {
    return await fn();
  } catch (error) {
    logger.error(`Error in ${label}`, error);
    if (retryCount > 0) {
      logger.debug(`Retrying ${label}`, { attempts_left: retryCount });
      await new Promise((r) => setTimeout(r, 1000));
      return withRetry(fn, label, retryCount - 1);
    }
    throw error;
  }
};

const getStart = (x) => x?.booked_dates?.start_date || x?.start_date || 'N/A';
const getEnd = (x) => x?.booked_dates?.end_date || x?.end_date || 'N/A';

const getTotal = (x) => {
  const qty = Number(x?.quantity || 0);
  const rate = Number(x?.item?.hourly_rate || 0);
  const total = qty * rate;
  return formatPrice(total);
};

// -------------------- API fetches --------------------
const fetchUserEquipments = async () => {
  await Promise.all([
    store.fetchUserEquipments(),
    store.fetchUserEditableEquipments(),
  ]);
};

const fetchOrderItems = async () => {
  const response = await axios.get(`${api_base_url}/api/order-items/`, {
    withCredentials: true,
  });

  orderItems.value = Array.isArray(response.data) ? response.data : [];

  // (optional) quick sanity log
  logger.debug('Order items loaded', { count: orderItems.value.length });
};

const fetchCategories = async () => {
  await store.fetchCategories();
  logger.debug('Categories loaded', { count: store.categories.length });
};

// -------------------- efficient status buckets (one pass, sorted by date) --------------------
// Within mixed-status buckets, actionable statuses sort first
const actionPriority = { return: 0, pickup: 0, pending: 0, paid: 1, rented: 1, approved: 1, disputed: 1, returned: 2, completed: 2, canceled: 3, rejected: 3 };
const sortByAttentionThenDate = (a, b) => {
  const pa = actionPriority[a?.status] ?? 9;
  const pb = actionPriority[b?.status] ?? 9;
  if (pa !== pb) return pa - pb;
  return new Date(b?.start_date || 0) - new Date(a?.start_date || 0);
};

const lists = computed(() => {
  const items = Array.isArray(orderItems.value) ? orderItems.value : [];

  const out = {
    paid: [],
    pending: [],
    awaitingPickup: [], // approved + pickup
    rented: [], // rented + return
    returned: [], // returned + completed
    disputed: [],
    canceledRejected: [], // canceled + rejected
    other: [],
  };

  for (const x of items) {
    const s = x?.status;

    if (s === 'paid') out.paid.push(x);
    else if (s === 'pending') out.pending.push(x);
    else if (s === 'approved' || s === 'pickup') out.awaitingPickup.push(x);
    else if (s === 'rented' || s === 'return') out.rented.push(x);
    else if (s === 'returned' || s === 'completed') out.returned.push(x);
    else if (s === 'disputed') out.disputed.push(x);
    else if (s === 'canceled' || s === 'rejected') out.canceledRejected.push(x);
    else out.other.push(x);
  }

  // Sort each bucket: actionable items first, then by date (most recent first)
  for (const key of Object.keys(out)) {
    out[key].sort(sortByAttentionThenDate);
  }

  return out;
});

// Lock editing if an equipment is involved in any “active-ish” order item
const isLockedByRental = (equipmentId) => {
  const items = Array.isArray(orderItems.value) ? orderItems.value : [];
  const activeStatuses = new Set([
    'paid',
    'pending',
    'approved',
    'pickup',
    'rented',
    'return',
    'disputed',
    // include returned/completed if you want to block edit after return; remove if not desired
    // 'returned',
    // 'completed',
  ]);

  return items.some((x) => activeStatuses.has(x?.status) && x?.item?.id === equipmentId);
};

// -------------------- lifecycle --------------------
onMounted(async () => {
  try {
    loading.value = true;

    if (!authStore.user) {
      logger.warn('No user authenticated, redirecting to login');
      showNotification('Error', 'Please log in to view your items and rentals.', 'error');
      router.push('/login');
      return;
    }

    await Promise.all([
      withRetry(fetchUserEquipments, 'fetchUserEquipments'),
      withRetry(fetchOrderItems, 'fetchOrderItems'),
      withRetry(fetchCategories, 'fetchCategories'),
    ]);
  } catch (error) {
    logger.error('Error during mount', error);
    showNotification('Error', 'Failed to initialize data. Please try again.', 'error');
  } finally {
    loading.value = false;
  }
});

// -------------------- navigation --------------------
const goToDetail = (equipmentId) => {
  if (equipmentId) router.push({ name: 'equipment-details', params: { id: equipmentId } });
  else showNotification('Error', 'Invalid item ID.', 'error');
};

// -------------------- edit modal address autocomplete --------------------
const onEditAddrInput = () => {
  fetchEditAddrSuggestions(editedEquipment.value.address?.street_address);
};

const selectEditAddrSuggestion = async (suggestion) => {
  clearEditAddrSuggestions();
  editedEquipment.value.address.street_address = suggestion.full;
  const result = await resolveEditAddrPlace(suggestion.placeId, suggestion._prediction);
  if (!result) return;

  editedEquipment.value.address = {
    street_address: result.street_address || result.formatted || suggestion.full,
    city: result.city || '',
    state: result.state || '',
    zip_code: result.zip_code || '',
    country: result.country || '',
  };
};

const openEditModal = async (equipment) => {
  try {
    const response = await axios.get(`${api_base_url}/api/equipments/${equipment.id}/`, {
      withCredentials: true,
    });

    const data = response.data;

    editedEquipment.value = {
      ...data,
      category: data.category?.id || data.category,
      current_images: data.current_images || [],
      address: data.address || {
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
      },
    };

    // Initialise tags input (comma separated)
    let tagNames = [];
    if (Array.isArray(data.tags)) {
      tagNames = data.tags.map((t) => (typeof t === 'string' ? t : t.name)).filter(Boolean);
    } else if (typeof data.tags === 'string') {
      tagNames = [data.tags];
    } else if (data.tags && typeof data.tags === 'object' && data.tags.name) {
      tagNames = [data.tags.name];
    }
    tagsInput.value = tagNames.join(', ');

    // Initialise specifications array
    const specsRaw = data.specifications || [];
    specifications.value = Array.isArray(specsRaw)
      ? specsRaw.map((s) => ({
          name: s.name || s.key || '',
          value: s.value || s.val || '',
        }))
      : [];

    newImages.value = [];
    removeImageIds.value = [];
    editModalVisible.value = true;

    // Initialize Quill editors after DOM renders
    await nextTick();
    initEditQuillEditors();
  } catch (error) {
    logger.error('Error fetching equipment details', error);
    showNotification('Error', 'Failed to load equipment details.', 'error');
  }
};

const closeEditModal = () => {
  // Destroy Quill instances
  if (editDescriptionQuill) {
    editDescriptionQuill = null;
  }
  if (editTermsQuill) {
    editTermsQuill = null;
  }
  editModalVisible.value = false;
  editedEquipment.value = {};
  newImages.value = [];
  removeImageIds.value = [];
  tagsInput.value = '';
  specifications.value = [];
  if (editAutocompleteInstance) {
    google.maps.event.clearInstanceListeners(editAutocompleteInstance);
    editAutocompleteInstance = null;
  }
};

const initEditQuillEditors = () => {
  const opts = (placeholder) => ({
    theme: 'snow',
    placeholder,
    modules: { toolbar: quillToolbarOptions },
  });

  if (editDescriptionEditorRef.value) {
    // Clear previous instance content
    editDescriptionEditorRef.value.innerHTML = '';
    editDescriptionQuill = new Quill(editDescriptionEditorRef.value, opts('Describe your item...'));
    if (editedEquipment.value.description) {
      editDescriptionQuill.root.innerHTML = editedEquipment.value.description;
    }
    editDescriptionQuill.on('text-change', () => {
      editedEquipment.value.description = editDescriptionQuill.root.innerHTML;
    });
  }

  if (editTermsEditorRef.value) {
    editTermsEditorRef.value.innerHTML = '';
    editTermsQuill = new Quill(editTermsEditorRef.value, opts('Rental terms & conditions...'));
    if (editedEquipment.value.terms) {
      editTermsQuill.root.innerHTML = editedEquipment.value.terms;
    }
    editTermsQuill.on('text-change', () => {
      editedEquipment.value.terms = editTermsQuill.root.innerHTML;
    });
  }
};

const handleImageUpload = (event) => {
  const files = Array.from(event.target.files);
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

  const invalidFiles = files.filter((file) => !allowedTypes.includes(file.type));
  if (invalidFiles.length > 0) {
    showNotification('Error', 'Only JPEG, JPG, and PNG images are allowed.', 'error');
    newImages.value = [];
    event.target.value = '';
    return;
  }

  const existingCount = editedEquipment.value.current_images?.length || 0;
  const totalAfterUpload = existingCount + files.length;

  if (totalAfterUpload > 4) {
    showNotification(
      'Error',
      `You can only have up to 4 images in total. You currently have ${existingCount} and tried to add ${files.length}.`,
      'error',
    );
    newImages.value = [];
    event.target.value = '';
    return;
  }

  newImages.value = files;
};

const removeImage = (imageId) => {
  if (!removeImageIds.value.includes(imageId)) {
    removeImageIds.value = [...removeImageIds.value, imageId];
  }
  editedEquipment.value.current_images = editedEquipment.value.current_images.filter(
    (img) => img.id !== imageId,
  );
};

// Specifications helpers
const addSpecificationRow = () => {
  specifications.value.push({ name: '', value: '' });
};
const removeSpecificationRow = (index) => {
  specifications.value.splice(index, 1);
};

const updateEquipment = async () => {
  if (
    !editedEquipment.value.name ||
    !editedEquipment.value.hourly_rate ||
    !editedEquipment.value.available_quantity
  ) {
    showNotification('Error', 'Please fill in all required fields.', 'error');
    return;
  }

  const tagList = tagsInput.value
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length);

  const cleanedSpecs = specifications.value
    .map((s) => ({
      name: s.name ? s.name.trim() : '',
      value: s.value ? s.value.trim() : '',
    }))
    .filter((s) => s.name || s.value);

  isUpdating.value = true;

  const formData = new FormData();
  formData.append('name', editedEquipment.value.name);
  formData.append('hourly_rate', editedEquipment.value.hourly_rate);
  formData.append('available_quantity', editedEquipment.value.available_quantity);
  formData.append('description', editedEquipment.value.description || '');
  formData.append('category', editedEquipment.value.category);

  const boolValue =
    editedEquipment.value.is_available === true || editedEquipment.value.is_available === 'true'
      ? 'true'
      : 'false';
  formData.append('is_available', boolValue);

  if (editedEquipment.value.terms !== undefined && editedEquipment.value.terms !== null) {
    formData.append('terms', editedEquipment.value.terms);
  }

  tagList.forEach((tag) => formData.append('tags', tag));

  if (editedEquipment.value.address) {
    const addr = editedEquipment.value.address;
    if (addr.street_address) formData.append('street_address', addr.street_address);
    if (addr.city) formData.append('city', addr.city);
    if (addr.state) formData.append('state', addr.state);
    if (addr.zip_code) formData.append('zip_code', addr.zip_code);
    if (addr.country) formData.append('country', addr.country);
  }

  if (cleanedSpecs.length) formData.append('specifications', JSON.stringify(cleanedSpecs));

  if (newImages.value.length > 0) {
    newImages.value.forEach((image) => formData.append('images', image));
  }

  if (removeImageIds.value.length > 0) {
    formData.append('remove_images', JSON.stringify(removeImageIds.value));
  }

  try {
    const response = await axios.put(
      `${api_base_url}/api/equipments/${editedEquipment.value.id}/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true },
    );

    store.userEquipments = store.userEquipments.map((eq) =>
      eq.id === editedEquipment.value.id ? response.data : eq,
    );

    showNotification('Success', 'Item updated successfully!', 'success');
    closeEditModal();
  } catch (error) {
    // If it's a network/API error, show failure; otherwise the update succeeded
    if (error?.response || error?.request) {
      logger.error('Error updating item', error);
      showNotification('Error', error?.response?.data?.detail || 'Failed to update Item. Please try again.', 'error');
    } else {
      // Non-API error (e.g. from closeEditModal) — update already succeeded
      logger.warn('Post-update cleanup error (update succeeded)', error);
      showNotification('Success', 'Item updated successfully!', 'success');
      closeEditModal();
    }
  } finally {
    isUpdating.value = false;
  }
};

// -------------------- return confirmation --------------------
const openReturnModal = (orderItemId) => {
  selectedOrderItem.value = orderItemId;
  showReturnModal.value = true;
};

const closeReturnModal = () => {
  showReturnModal.value = false;
  selectedOrderItem.value = null;
};

const handleReturnConfirmation = async (returnData) => {
  if (!returnData.orderItemId) {
    showNotification('Error', 'Invalid order item ID.', 'error');
    return;
  }

  const formData = new FormData();
  formData.append('returnCondition', returnData.returnCondition);
  if (returnData.complaintText) formData.append('complaintText', returnData.complaintText);
  if (returnData.image) formData.append('image', returnData.image.blob, returnData.image.filename);

  try {
    await axios.post(
      `${api_base_url}/api/order-items/${returnData.orderItemId}/confirm_return/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true },
    );

    const message =
      returnData.returnCondition === 'damaged'
        ? 'Item return recorded as damaged and marked as disputed.'
        : 'Item return confirmed successfully!';

    showNotification('Success', message, 'success');
    await withRetry(fetchOrderItems, 'fetchOrderItems');
  } catch (error) {
    logger.error('Error confirming return', error);
    const errorMessage =
      error.response?.data?.error ||
      (typeof error.response?.data === 'string'
        ? error.response.data
        : error.response?.data
          ? JSON.stringify(error.response.data)
          : error.message || 'Failed to confirm return. Please try again.');
    showNotification('Error', errorMessage, 'error');
  } finally {
    showReturnModal.value = false;
    selectedOrderItem.value = null;
  }
};

// -------------------- approve rental --------------------
const openApprovalModal = (rental) => {
  selectedRental.value = rental;
  isApprovalModalOpen.value = true;
};

const approveRentalApproval = async (rentalId) => {
  if (!rentalId) {
    showNotification('Error', 'Invalid rental ID.', 'error');
    return;
  }

  try {
    await axios.post(`${api_base_url}/api/order-items/${rentalId}/approve/`, {}, { withCredentials: true });
    await withRetry(fetchOrderItems, 'fetchOrderItems');
    showNotification('Success', 'Rental approved successfully!', 'success');
    isApprovalModalOpen.value = false;
  } catch (error) {
    logger.error('Error approving rental', error);
    showNotification('Error', 'Rental approval failed. Please try again.', 'error');
  }
};

// -------------------- pickup confirmation --------------------
const openPickupConfirmationModal = (rentalId) => {
  selectedOrderItem.value = rentalId;
  showNewPickupConfirmationModal.value = true;
};

const handlePickupConfirmation = async () => {
  try {
    await withRetry(fetchOrderItems, 'fetchOrderItems');
    showNewPickupConfirmationModal.value = false;
    selectedOrderItem.value = null;
    showNotification('Success', 'Pickup confirmed successfully!', 'success');
  } catch (error) {
    logger.error('Error confirming pickup', error);
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.detail ||
      error.response?.data?.message ||
      (typeof error.response?.data === 'string'
        ? error.response.data
        : error.response?.data
          ? JSON.stringify(error.response.data)
          : error.message || 'Failed to confirm pickup. Please try again.');
    showNotification('Error', errorMessage, 'error');
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.animate-pop {
  animation: pop 0.3s ease-out;
}
@keyframes pop {
  0% {
    transform: scale(0.95);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Quill editor theming */
.quill-editor-wrap :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid var(--color-input-border, #e5e7eb);
  background: var(--color-bg2, #f9fafb);
  border-radius: 0.5rem 0.5rem 0 0;
}

.quill-editor-wrap :deep(.ql-container) {
  border: none;
  min-height: 140px;
  font-size: 0.875rem;
}

.quill-editor-wrap :deep(.ql-editor) {
  min-height: 140px;
  color: var(--color-input-text, inherit);
}

.quill-editor-wrap :deep(.ql-editor.ql-blank::before) {
  color: var(--color-muted, #9ca3af);
  font-style: normal;
}

.quill-editor-wrap :deep(.ql-stroke) {
  stroke: var(--color-text2, #6b7280);
}

.quill-editor-wrap :deep(.ql-fill) {
  fill: var(--color-text2, #6b7280);
}

.quill-editor-wrap :deep(.ql-picker-label) {
  color: var(--color-text2, #6b7280);
}

.quill-editor-wrap :deep(.ql-picker-options) {
  background: var(--color-input, #fff);
  border-color: var(--color-input-border, #e5e7eb);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.quill-editor-wrap :deep(.ql-picker-item) {
  color: var(--color-text, #1c1c1c);
}

.quill-editor-wrap :deep(.ql-picker-item:hover) {
  color: var(--color-primary-600, #e68d00);
}

.quill-editor-wrap :deep(.ql-editor h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.5em 0;
}

.quill-editor-wrap :deep(.ql-editor h2) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0.4em 0;
}

.quill-editor-wrap :deep(.ql-editor h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.35em 0;
}

.quill-editor-wrap :deep(.ql-editor blockquote) {
  border-left: 3px solid var(--color-primary-600, #e68d00);
  padding-left: 0.75rem;
  color: var(--color-text2, #6b7280);
  margin: 0.5em 0;
}

.quill-editor-wrap :deep(.ql-editor a) {
  color: var(--color-primary-600, #e68d00);
  text-decoration: underline;
}

.quill-editor-wrap :deep(.ql-editor ol),
.quill-editor-wrap :deep(.ql-editor ul) {
  padding-left: 1.5em;
}

.quill-editor-wrap :deep(.ql-snow .ql-tooltip) {
  background: var(--color-input, #fff);
  border-color: var(--color-input-border, #e5e7eb);
  color: var(--color-text, #1c1c1c);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-radius: 0.5rem;
  z-index: 10;
}

.quill-editor-wrap :deep(.ql-snow .ql-tooltip input[type="text"]) {
  background: var(--color-bg2, #f9fafb);
  border-color: var(--color-input-border, #e5e7eb);
  color: var(--color-input-text, inherit);
  border-radius: 0.375rem;
}

.quill-editor-wrap :deep(.ql-snow .ql-tooltip a.ql-action::after),
.quill-editor-wrap :deep(.ql-snow .ql-tooltip a.ql-remove::before) {
  color: var(--color-primary-600, #e68d00);
}
</style>
